'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { X, Minus, Plus, Check, ChevronLeft, CalendarCheck } from 'lucide-react'
import { PhoneInput } from '@/components/ui'
import {
  groupSlotsByPeriod,
  type Period,
  type SlotGroup,
} from '@/lib/reservations/slotFormat'
import { validateBookingForm, toE164, reservationErrorKey } from '@/lib/reservations/bookingForm'
import { requiresDeposit, type DepositSettings } from '@/lib/reservations/deposit'
import { ReservationDepositCapture } from './ReservationDepositCapture'

type Props = {
  open: boolean
  onClose: () => void
  slug: string
  accentColor?: string
  /** tz de la location, ej. "America/Chicago". */
  tz: string
  /** Horizonte máximo de días para reservar (para limitar el selector). */
  maxAdvanceDays?: number
  /** Config de depósito (card-on-file). Si el grupo lo requiere, se inserta un paso
   *  de captura ANTES de crear la reserva. Ausente/deshabilitado = flujo normal. */
  depositSettings?: DepositSettings
}

type Step = 'when' | 'details' | 'deposit' | 'done'

/** "YYYY-MM-DD" de hoy en la tz de la location (en-CA produce ese formato). */
function todayInTz(tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/** Suma días a un "YYYY-MM-DD" y devuelve "YYYY-MM-DD" (UTC-safe). */
function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/**
 * Flujo de reserva del cliente (3 pasos: cuándo → datos → confirmado).
 * Se monta vía portal a document.body (escapa del stacking del nav fijo),
 * panel claro como DeliveryModal. Consume el motor del Plan 1:
 *   GET /api/reservations/availability  · slots reales
 *   POST /api/reservations              · creación atómica + SMS
 */
export function ReservationModal({ open, onClose, slug, accentColor, tz, maxAdvanceDays = 60, depositSettings }: Props) {
  const t = useTranslations('public.microsite')
  const accent = accentColor ?? '#ea580c'

  const [step, setStep] = useState<Step>('when')
  const [party, setParty] = useState(2)
  const [date, setDate] = useState<string>(() => todayInTz(tz))
  const [groups, setGroups] = useState<SlotGroup[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedIso, setSelectedIso] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [requests, setRequests] = useState('')
  const [optIn, setOptIn] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState<{ name?: string; phone?: string; partySize?: string }>({})
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<{ code: string; token: string } | null>(null)
  // Intent FIRMADO del depósito (de la captura). Se reenvía a POST /api/reservations
  // junto con el consentimiento; NUNCA contiene un BRIC. Su presencia también evita
  // re-mostrar el paso de captura si el cliente vuelve atrás y reintenta.
  const [depositIntentToken, setDepositIntentToken] = useState<string | null>(null)
  // Se incrementa para forzar un re-fetch de disponibilidad (ej. tras un 409: el
  // slot elegido se ocupó y hay que recargar el grid con datos frescos).
  const [reloadKey, setReloadKey] = useState(0)

  // Cerrar con Escape + bloquear scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // Cargar disponibilidad real al cambiar party/date (con el modal abierto, paso "when").
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingSlots(true)
    setSelectedIso(null)
    setApiErrorKey(null)
    fetch(`/api/reservations/availability?slug=${encodeURIComponent(slug)}&date=${date}&party=${party}`)
      .then((r) => r.json())
      .then((data: { slots?: { timeIso: string; available: boolean }[]; reason?: string }) => {
        if (cancelled) return
        if (data.reason === 'party_too_large') {
          setApiErrorKey('reservationErrorPartyTooLarge')
          setGroups([])
          return
        }
        setGroups(groupSlotsByPeriod(data.slots ?? [], tz))
      })
      .catch(() => {
        if (!cancelled) setApiErrorKey('reservationErrorGeneric')
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, slug, date, party, tz, reloadKey])

  function resetAndClose() {
    onClose()
    // Reset diferido para no parpadear el contenido durante la animación de salida.
    setTimeout(() => {
      setStep('when')
      setSelectedIso(null)
      setName('')
      setPhone('')
      setRequests('')
      setOptIn(true)
      setFormErrors({})
      setApiErrorKey(null)
      setConfirmation(null)
      setParty(2)
      setDepositIntentToken(null)
    }, 250)
  }

  /**
   * Crea la reserva (POST /api/reservations). Si `intentToken` viene, adjunta el
   * depósito (intent firmado + consentimiento dado afirmativamente en la captura).
   * El servidor resuelve el BRIC desde el intent; el cliente nunca lo toca.
   */
  async function doCreate(intentToken?: string) {
    if (!selectedIso) return
    setSubmitting(true)
    setApiErrorKey(null)
    try {
      const res = await fetch('/api/reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          scheduled_at: selectedIso,
          party_size: party,
          customer_name: name.trim(),
          customer_phone: toE164(phone),
          special_requests: requests.trim() || undefined,
          opt_in: optIn,
          ...(intentToken ? { deposit_intent_token: intentToken, deposit_consent: true } : {}),
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setApiErrorKey(reservationErrorKey(res.status, body?.error ?? ''))
        // El slot se ocupó / dejó de ser válido: limpiá la selección, recargá los
        // horarios frescos (reloadKey) y volvé al paso de elegir hora para re-elegir.
        if (res.status === 409 || body?.error === 'invalid_slot') {
          setSelectedIso(null)
          setReloadKey((k) => k + 1)
          setStep('when')
        }
        return
      }
      const body = (await res.json()) as { reservation: { confirmation_code: string }; manage_token: string }
      setConfirmation({ code: body.reservation.confirmation_code, token: body.manage_token })
      setStep('done')
    } catch {
      setApiErrorKey('reservationErrorGeneric')
    } finally {
      setSubmitting(false)
    }
  }

  async function submit() {
    const v = validateBookingForm({ name, phone, partySize: party })
    setFormErrors(v.errors)
    if (!v.valid || !selectedIso) return
    // Grupo que requiere depósito y sin tarjeta aún: pedir la captura ANTES de crear.
    // El intent se obtiene en el paso 'deposit' (la creación corre tras onCaptured).
    if (depositSettings && requiresDeposit(party, depositSettings) && !depositIntentToken) {
      setApiErrorKey(null)
      setStep('deposit')
      return
    }
    await doCreate(depositIntentToken ?? undefined)
  }

  if (typeof document === 'undefined') return null

  const periodLabel = (p: Period) =>
    t(`reservationPeriod${p.charAt(0).toUpperCase()}${p.slice(1)}` as 'reservationPeriodMorning')

  // El paso de garantía (captura de tarjeta) usa un panel a pantalla completa: el
  // iframe hosted de EPX necesita aire, no el modal angosto (max-w-md) de los demás pasos.
  const isDeposit = step === 'deposit'

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={t('reservationTitle')}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={resetAndClose} />

          <motion.div
            className={`relative w-full overflow-y-auto bg-white shadow-2xl ${
              isDeposit
                ? 'h-[100dvh] sm:h-auto sm:max-w-3xl max-h-[100dvh] sm:max-h-[94vh] rounded-none sm:rounded-3xl p-5 sm:p-8'
                : 'sm:max-w-md max-h-[92vh] rounded-t-3xl sm:rounded-3xl p-6 sm:p-7'
            }`}
            style={{ color: '#171717' }}
            initial={{ y: 28, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 28, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <button
              type="button"
              onClick={resetAndClose}
              aria-label={t('reservationClose')}
              className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:bg-black/5 transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* ─── Paso 1: cuándo ─────────────────────────────────────── */}
            {step === 'when' && (
              <div>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-4" style={{ background: accent }}>
                  <CalendarCheck className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold pr-8">{t('reservationTitle')}</h2>
                <p className="mt-1 text-sm text-gray-500">{t('reservationSubtitle')}</p>

                {/* Party */}
                <p className="mt-5 text-xs font-bold uppercase tracking-wide text-gray-400">{t('reservationParty')}</p>
                <div className="mt-2 flex items-center justify-between rounded-2xl border border-gray-200 px-4 py-3">
                  <button
                    type="button"
                    aria-label="-"
                    onClick={() => setParty((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-base font-bold">{party} {t('reservationPartyUnit')}</span>
                  <button
                    type="button"
                    aria-label="+"
                    onClick={() => setParty((p) => Math.min(20, p + 1))}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Date */}
                <p className="mt-4 text-xs font-bold uppercase tracking-wide text-gray-400">{t('reservationDate')}</p>
                <input
                  type="date"
                  value={date}
                  min={todayInTz(tz)}
                  max={addDays(todayInTz(tz), maxAdvanceDays)}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base"
                />

                {/* Time */}
                <p className="mt-4 text-xs font-bold uppercase tracking-wide text-gray-400">{t('reservationTime')}</p>
                {apiErrorKey && (
                  <p className="mt-2 text-sm text-red-500">{t(apiErrorKey as 'reservationErrorGeneric')}</p>
                )}
                {loadingSlots ? (
                  <p className="mt-2 text-sm text-gray-400">…</p>
                ) : groups.length === 0 ? (
                  apiErrorKey ? null : <p className="mt-2 text-sm text-gray-500">{t('reservationNoSlots')}</p>
                ) : (
                  <div className="mt-2 flex flex-col gap-3">
                    {groups.map((g) => (
                      <div key={g.period}>
                        <p className="text-[11px] font-semibold text-gray-400 mb-1">{periodLabel(g.period)}</p>
                        <div className="flex flex-wrap gap-2">
                          {g.slots.map((s) => {
                            const selected = s.timeIso === selectedIso
                            return (
                              <button
                                key={s.timeIso}
                                type="button"
                                disabled={!s.available}
                                onClick={() => setSelectedIso(s.timeIso)}
                                className="rounded-xl px-3 py-2 text-sm font-semibold border transition disabled:opacity-40 disabled:line-through"
                                style={
                                  selected
                                    ? { backgroundColor: accent, color: '#fff', borderColor: accent }
                                    : { backgroundColor: '#fff', color: '#171717', borderColor: '#e5e7eb' }
                                }
                              >
                                {s.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  disabled={!selectedIso}
                  onClick={() => setStep('details')}
                  className="mt-6 w-full rounded-2xl px-5 py-4 text-base font-bold text-white transition hover:opacity-90 disabled:opacity-40"
                  style={{ backgroundColor: accent }}
                >
                  {t('reservationStepDetails')}
                </button>
              </div>
            )}

            {/* ─── Paso 2: datos ──────────────────────────────────────── */}
            {step === 'details' && (
              <div>
                <button
                  type="button"
                  onClick={() => setStep('when')}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" /> {t('reservationBack')}
                </button>
                <h2 className="mt-3 text-xl font-bold">{t('reservationStepDetails')}</h2>

                <div className="mt-4 flex flex-col gap-3">
                  <div>
                    <input
                      type="text"
                      placeholder={t('reservationName')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base"
                    />
                    {formErrors.name && <p className="mt-1 text-xs text-red-500">{t(formErrors.name as 'reservationErrorNameRequired')}</p>}
                  </div>
                  <div>
                    <PhoneInput
                      placeholder={t('reservationPhone')}
                      value={phone}
                      onChange={setPhone}
                      className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base"
                    />
                    {formErrors.phone && <p className="mt-1 text-xs text-red-500">{t(formErrors.phone as 'reservationErrorPhoneInvalid')}</p>}
                  </div>
                  <textarea
                    placeholder={t('reservationRequestsPlaceholder')}
                    value={requests}
                    onChange={(e) => setRequests(e.target.value)}
                    rows={2}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base resize-none"
                  />
                  <label className="flex items-start gap-2 text-xs text-gray-500 leading-snug">
                    <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-0.5" />
                    <span>{t('reservationOptIn')}</span>
                  </label>
                </div>

                {apiErrorKey && <p className="mt-3 text-sm text-red-500">{t(apiErrorKey as 'reservationErrorGeneric')}</p>}

                <button
                  type="button"
                  disabled={submitting}
                  onClick={submit}
                  className="mt-5 w-full rounded-2xl px-5 py-4 text-base font-bold text-white transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: accent }}
                >
                  {t('reservationConfirm')}
                </button>
              </div>
            )}

            {/* ─── Paso 2.5: depósito (SALE real al reservar) ─────────── */}
            {step === 'deposit' && selectedIso && (
              <div className="mx-auto w-full max-w-xl">
                <button
                  type="button"
                  onClick={() => setStep('details')}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" /> {t('reservationBack')}
                </button>
                <h2 className="mt-3 text-xl font-bold">{t('reservationDepositStepTitle')}</h2>
                <p className="mt-1 text-sm text-gray-500">{t('reservationDepositStepBody')}</p>

                <div className="mt-4">
                  <ReservationDepositCapture
                    slug={slug}
                    partySize={party}
                    scheduledAt={selectedIso}
                    accentColor={accent}
                    onCaptured={(tok) => {
                      setDepositIntentToken(tok)
                      void doCreate(tok)
                    }}
                    onError={() => setApiErrorKey('reservationErrorGeneric')}
                  />
                </div>

                {apiErrorKey && <p className="mt-3 text-sm text-red-500">{t(apiErrorKey as 'reservationErrorGeneric')}</p>}
              </div>
            )}

            {/* ─── Paso 3: confirmado ─────────────────────────────────── */}
            {step === 'done' && confirmation && (
              <div className="text-center py-2">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(46,204,113,0.14)', color: '#1B8A4E' }}>
                  <Check className="w-8 h-8" />
                </div>
                <h2 className="text-2xl font-bold">{t('reservationSuccessTitle')}</h2>
                <p className="mt-1 text-sm text-gray-500">{t('reservationSuccessBody', { name: name.trim() })}</p>
                <div className="mt-4 rounded-2xl border border-gray-200 px-5 py-3">
                  <p className="text-sm font-bold">{t('reservationSuccessCode', { code: confirmation.code })}</p>
                </div>
                <p className="mt-3 text-xs font-semibold" style={{ color: '#1B8A4E' }}>{t('reservationSuccessSms')}</p>
                {confirmation.token && (
                  <a
                    href={`/reserva/${confirmation.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 block w-full rounded-2xl px-5 py-4 text-base font-bold text-white text-center transition hover:opacity-90"
                    style={{ backgroundColor: accent }}
                  >
                    {t('reservationManageCta')}
                  </a>
                )}
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="mt-3 w-full rounded-2xl px-5 py-3 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
                >
                  {t('reservationClose')}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
